import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import allImages from "../../assets/images-import";
import { resetPassword } from "../../services/authService";
import { validatePasswordForm } from "../../utils/validation";

const REDIRECT_DELAY_MS = 2500;

export const PasswordChangeContent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    if (!resetSuccess) return undefined;

    const redirectTimer = setTimeout(() => {
      navigate("/auth/signin");
    }, REDIRECT_DELAY_MS);

    return () => clearTimeout(redirectTimer);
  }, [resetSuccess, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!token) {
      setSubmitError("Invalid or missing reset token. Please request a new reset link.");
      return;
    }

    const validationErrors = validatePasswordForm(newPassword, confirmPassword);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});

    try {
      setIsSubmitting(true);
      const response = await resetPassword({
        token,
        newPassword,
        confirmPassword,
      });

      if (response?.isSuccess) {
        const successMessage =
          response.message || "Password reset successfully.";
        toast.success(successMessage);
        setResetSuccess(true);
      } else {
        setSubmitError(response?.message || "Unable to reset password.");
      }
    } catch (err) {
      const apiMessage =
        err.response?.data?.message ||
        err.message ||
        "Unable to reset password. Please try again.";
      setSubmitError(apiMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
      <div className="auth-page-content overflow-hidden pt-lg-5">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div
                className="card overflow-hidden"
                style={{ boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px" }}
              >
                <div className="row g-0 aligning-item">
                  <div className="col-lg-6">
                    <div
                      className="p-lg-5 p-4 auth-one-bg"
                      style={{
                        backgroundImage: `url(${allImages.SigninPageBack})`,
                        backgroundPosition: "center",
                        opacity: 0.9,
                        height: "540px",
                        backgroundRepeat: "no-repeat",
                        backgroundSize: "100%",
                      }}
                    ></div>
                  </div>

                  <div className="col-lg-6">
                    <div className="card-body p-md-5 mx-md-4">
                      <div className="text-center mt-2 mb-2">
                        <Link
                          to="/auth/signin"
                          className="d-inline-block auth-logo"
                          style={{ marginBottom: "30px" }}
                        >
                          <img
                            src={allImages.logoDark}
                            alt="Logo"
                            height="60"
                          />
                        </Link>
                      </div>

                      <div className="text-center">
                        <h4
                          className="fw-bolder"
                          style={{
                            color: "#000",
                            fontSize: "16px",
                            marginBottom: "4px",
                          }}
                        >
                          Reset Password
                        </h4>
                        <p className="text-muted mb-0">
                          Enter your new password below
                        </p>
                      </div>

                      <form onSubmit={handleSubmit} className="mt-4">
                        {!token && (
                          <div className="alert alert-danger" role="alert">
                            Invalid or missing reset token. Please use the link
                            from your email or{" "}
                            <Link to="/auth/password-reset">request a new one</Link>.
                          </div>
                        )}

                        {resetSuccess && (
                          <div className="alert alert-success" role="alert">
                            Password reset successfully. Redirecting to sign in...
                          </div>
                        )}

                        {submitError && (
                          <div className="alert alert-danger" role="alert">
                            {submitError}
                          </div>
                        )}

                        <div className="mb-3">
                          <label htmlFor="newPassword" className="form-label">
                            New Password
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type={isPasswordVisible ? "text" : "password"}
                              className={`form-control ${
                                errors.newPassword ? "is-invalid" : ""
                              }`}
                              id="newPassword"
                              style={{ minHeight: "50px" }}
                              placeholder="Enter new password"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              disabled={!token || resetSuccess}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setIsPasswordVisible(!isPasswordVisible)
                              }
                              className="viewhidePassword"
                            >
                              <i
                                className={`ri-${
                                  isPasswordVisible ? "eye-line" : "eye-off-line"
                                }`}
                              ></i>
                            </button>
                          </div>
                          {errors.newPassword && (
                            <div className="text-danger mt-1">
                              {errors.newPassword}
                            </div>
                          )}
                        </div>

                        <div className="mb-4">
                          <label
                            htmlFor="confirmPassword"
                            className="form-label"
                          >
                            Confirm Password
                          </label>
                          <div style={{ position: "relative" }}>
                            <input
                              type={
                                isConfirmPasswordVisible ? "text" : "password"
                              }
                              className={`form-control ${
                                errors.confirmPassword ? "is-invalid" : ""
                              }`}
                              id="confirmPassword"
                              style={{ minHeight: "50px" }}
                              placeholder="Confirm new password"
                              value={confirmPassword}
                              onChange={(e) =>
                                setConfirmPassword(e.target.value)
                              }
                              disabled={!token || resetSuccess}
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setIsConfirmPasswordVisible(
                                  !isConfirmPasswordVisible
                                )
                              }
                              className="viewhidePassword"
                            >
                              <i
                                className={`ri-${
                                  isConfirmPasswordVisible
                                    ? "eye-line"
                                    : "eye-off-line"
                                }`}
                              ></i>
                            </button>
                          </div>
                          {errors.confirmPassword && (
                            <div className="text-danger mt-1">
                              {errors.confirmPassword}
                            </div>
                          )}
                        </div>

                        <div className="text-center pt-1 pb-1">
                          <button
                            type="submit"
                            className="btn btn-secondary btn-primary-login w-100"
                            disabled={isSubmitting || !token || resetSuccess}
                          >
                            {isSubmitting
                              ? "Resetting..."
                              : resetSuccess
                              ? "Redirecting..."
                              : "Reset Password"}
                          </button>
                        </div>
                      </form>

                      <div className="text-center mt-4">
                        <p className="mb-0 text-muted">
                          Remember your password?{" "}
                          <Link
                            to="/auth/signin"
                            className="fw-semibold text-decoration-underline"
                            style={{ color: "#4b38b3" }}
                          >
                            Back to login
                          </Link>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
